import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { AsyncButton, Button } from '@ynap/button';
import BEMHelper from '@ynap/bem-helper';
import { redirectToCheckout } from '@ynap/redirect-utils';
import ProductSummary from '../ProductSummary/ProductSummary';
import { trackAddToBag, trackGoToBag } from './analytics';
import { withAnalytics, safeBuildEventObj } from '@ynap/analytics';
import { withOptimizely } from '@ynap/optimizely-utils';
import track from 'react-tracking';

const bem = new BEMHelper('ProductSummaryWithAddToBag');

const dummyProduct = {
    badge: { label: '' },
    designerName: '',
    href: '',
    imgSrc: {
        primary: '',
        secondary: '',
    },
    name: '',
    price: { currency: { label: '', symbol: '' }, discountPercentage: null, finalPrice: null, originalPrice: null },
    productId: 0,
};

const ProductSummaryWithAddToBag = (props) => {
    const {
        messages,
        product,
        openProductDetail,
        loading: isFetching,
        addToCart,
        addToCartLoading,
        addToCartError,
        locale,
        skuPartNumber,
        children,
        productImageAspectRatio,
        tracking,
        analytics,
        hasDotAlert,
        className,
        classNameButton,
        optimizely,
        hidePrice = false,
        includeShoppingCart,
        enableRecommendations,
        country = '',
        isWishlist = false,
        addMicrodata,
        imageOverlayElement
    } = props;

    const { displayable, isBuyable } = product;

    const trackedAddToBag = (sku, setActionNotificationOnError, addToCartLoading) => {
        optimizely?.track('wl_addToCart');

        return addToCart(sku, { setActionNotificationOnError, addToCartLoading, includeShoppingCart })
            ?.then((response) => {
                if (response && response.type === 'ADD_TO_CART_SUCCESS') {
                    return tracking.trackEvent(safeBuildEventObj(trackAddToBag, analytics, skuPartNumber, 1, product));
                }
            })
            .catch((e) => e); // TODO Add raygun call for catch addToCart error.
    };

    const trackedGoToBag = (locale) => {
        tracking.trackEvent(safeBuildEventObj(trackGoToBag, analytics));
        return redirectToCheckout(locale);
    };

    const asyncState = {
        loading: addToCartLoading,
        primaryAction: {
            message: messages.ctaButtons.add(),
            click: () => trackedAddToBag(skuPartNumber, true, addToCartLoading),
        },
        secondaryAction: {
            message: messages.ctaButtons.goToBag(),
            click: () => trackedGoToBag(locale),
            timeout: 5000,
        },
        error: !!addToCartError,
    };

    const productToRender = isFetching ? dummyProduct : product;
    const onClick = displayable || enableRecommendations ? openProductDetail : null;
    const atbInsideProductSummary = isWishlist;

    const AddToBagCTA = useMemo(
        () => (
            <div onClick={(event) => event.stopPropagation()}>
                <AsyncButton
                    asyncState={asyncState}
                    className={bem('addToBagBtn', [atbInsideProductSummary && 'insideProductSummary'], classNameButton)}
                    type={atbInsideProductSummary && isBuyable ? 'primary' : 'secondary'}
                    disabled={!isBuyable}
                />
            </div>
        ),
        [isFetching, addToCartLoading],
    );

    const ViewRecommendationsCTA = useMemo(
        () => (
            <div onClick={(event) => event.stopPropagation()}>
                <Button
                    className={bem('addToBagBtn', [atbInsideProductSummary && 'insideProductSummary'], classNameButton)}
                    type={atbInsideProductSummary ? 'secondary' : 'primary'}
                    onClick={() => {
                        optimizely.track('wl_viewrecommendation');
                        onClick();
                    }}
                >
                    {messages?.ctaButtons?.viewRecommendations?.()}
                </Button>
            </div>
        ),
        [isFetching],
    );

    const showAddToBag = !isFetching && skuPartNumber && displayable && !(!isBuyable && enableRecommendations);
    const showWishlistRecommendations = !isFetching && skuPartNumber && (!displayable || !isBuyable) && enableRecommendations;

    const ctaBtn = showAddToBag ? AddToBagCTA : showWishlistRecommendations ? ViewRecommendationsCTA : null;

    return (
        <div className={bem()}>
            <ProductSummary
                product={productToRender}
                messages={messages}
                onClick={onClick}
                loading={isFetching}
                className={bem('productSummary', null, className)}
                productImageAspectRatio={productImageAspectRatio}
                locale={locale}
                hasDotAlert={hasDotAlert}
                hidePrice={hidePrice}
                isWishlist={isWishlist}
                country={country}
                addMicrodata={addMicrodata}
                CTABtn={atbInsideProductSummary ? ctaBtn : null}
                imageOverlayElement={imageOverlayElement}
            >
                {children}
            </ProductSummary>
            {!atbInsideProductSummary ? ctaBtn : null}
        </div>
    );
};

ProductSummaryWithAddToBag.propTypes = {
    messages: PropTypes.shape({
        ctaButtons: PropTypes.shape({
            add: PropTypes.func.isRequired,
            goToBag: PropTypes.func.isRequired,
            viewRecommendations: PropTypes.func.isRequired,
        }),
    }),
    locale: PropTypes.string.isRequired,
    product: PropTypes.object.isRequired,
    openProductDetail: PropTypes.func,
    loading: PropTypes.bool.isRequired,
    addToCart: PropTypes.func.isRequired,
    addToCartLoading: PropTypes.bool,
    addToCartError: PropTypes.string,
    skuPartNumber: PropTypes.string.isRequired,
    children: PropTypes.node,
    productImageAspectRatio: PropTypes.string,
    tracking: PropTypes.shape({
        trackEvent: PropTypes.func,
    }),
    analytics: PropTypes.object,
    hasDotAlert: PropTypes.bool,
    className: PropTypes.string,
    classNameButton: PropTypes.string,
    optimizely: PropTypes.shape({
        track: PropTypes.func,
    }),
    hidePrice: PropTypes.bool,
    includeShoppingCart: PropTypes.bool,
    enableRecommendations: PropTypes.bool,
    country: PropTypes.string,
    minimumPriceCountries: PropTypes.array,
    isWishlist: PropTypes.bool,
    addMicrodata: PropTypes.bool,
    imageOverlayElement: PropTypes.node,
};

export default track()(withAnalytics(withOptimizely(ProductSummaryWithAddToBag)));
